export const DEMO_TENANT_ACCESS_STORAGE_KEY = 'shadowland.demoTenantAccess.v1';

export function parseMap(raw) {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
  } catch {
    // Fall through to compact comma format.
  }
  return String(raw)
    .split(',')
    .map((pair) => pair.trim())
    .filter(Boolean)
    .reduce((acc, pair) => {
      const [key, value] = pair.split(':').map((part) => part?.trim());
      if (key && value) acc[key] = value;
      return acc;
    }, {});
}

function tenantRequiresDemoToken(tenant) {
  return Boolean(tenant?.demoAccess?.required);
}

function tokenForTenant(tenant, demoTokens = {}) {
  if (!tenant) return '';
  return String(
    tenant.demoAccess?.pathToken
    || demoTokens[tenant.id]
    || demoTokens[tenant.demoAccess?.tokenEnvKey]
    || ''
  ).trim();
}

function tokenMatches(tenant, token, demoTokens) {
  const expected = tokenForTenant(tenant, demoTokens);
  return Boolean(token && expected && token === expected);
}

function parseDemoPath(pathname = '') {
  const match = String(pathname).match(/^\/demo\/([a-z0-9-]+)\/([a-zA-Z0-9_-]{16,})(?:\/|$)/);
  if (!match) return null;
  return { tenantId: match[1], token: match[2] };
}

function readStoredDemoAccess(storage) {
  if (!storage?.getItem) return null;
  try {
    const parsed = JSON.parse(storage.getItem(DEMO_TENANT_ACCESS_STORAGE_KEY) || 'null');
    if (parsed && typeof parsed === 'object') return parsed;
  } catch {
    return null;
  }
  return null;
}

function writeStoredDemoAccess(storage, tenantId, token) {
  if (!storage?.setItem) return;
  try {
    storage.setItem(DEMO_TENANT_ACCESS_STORAGE_KEY, JSON.stringify({ tenantId, token }));
  } catch {
    // Storage may be disabled; the tokenized URL still works for this load.
  }
}

function canSelectTenantFromHost(tenant) {
  return Boolean(tenant && (!tenantRequiresDemoToken(tenant) || tenant.demoAccess?.allowHostSelection));
}

export function resolveTenantIdForLocation({
  tenants,
  envTenant,
  domainMapRaw,
  demoTokenMapRaw,
  demoTokens = {},
  hostname,
  pathname,
  storage,
}) {
  const tokenMap = { ...parseMap(demoTokenMapRaw), ...demoTokens };

  if (envTenant && tenants[envTenant]) return envTenant;

  const demoPath = parseDemoPath(pathname);
  if (demoPath) {
    const tenant = tenants[demoPath.tenantId];
    if (tenant && tokenMatches(tenant, demoPath.token, tokenMap)) {
      writeStoredDemoAccess(storage, demoPath.tenantId, demoPath.token);
      return demoPath.tenantId;
    }
  }

  const storedDemoAccess = readStoredDemoAccess(storage);
  if (storedDemoAccess?.tenantId && tenants[storedDemoAccess.tenantId]) {
    const tenant = tenants[storedDemoAccess.tenantId];
    if (tokenMatches(tenant, storedDemoAccess.token, tokenMap)) return storedDemoAccess.tenantId;
  }

  const domainMap = parseMap(domainMapRaw);
  const host = String(hostname || '').toLowerCase();
  const mappedTenantId = domainMap[host];
  if (mappedTenantId && canSelectTenantFromHost(tenants[mappedTenantId])) return mappedTenantId;

  const parts = host.split('.');
  if (parts.length >= 3) {
    const subdomainTenantId = parts[0];
    if (canSelectTenantFromHost(tenants[subdomainTenantId])) return subdomainTenantId;
  }

  return 'shadowland';
}
